import React from 'react';

import { AxiosError } from 'axios';
import { reverse } from 'named-urls';

import ActionCell, { ActionConfig } from 'components/Grid/components/ActionCell';
import { ColumnActions } from 'components/Grid/types';

import { EntityModules } from 'core/constants';
import routes from 'core/routes';

import API from 'utils/api';
import { apiErrorsWithKeys } from 'utils/api/errors';
import { useStore } from 'utils/hooks/store';

export type RiskAssessmentTemplateRow = { uuid: string; name?: string; isActive: boolean };

interface RiskAssessmentTemplateActionCellProps {
  row: RiskAssessmentTemplateRow;
  actions: ColumnActions;
}

const RiskAssessmentTemplateActionCell: React.FunctionComponent<RiskAssessmentTemplateActionCellProps> = ({
  row,
  actions,
}) => {
  const { routing, notification } = useStore();

  const setActive = async (isActive: boolean): Promise<void> => {
    try {
      await API.entities.updateEntityForm(EntityModules.RisksAssessments, row.uuid, { settings: { isActive } })();
      notification.enqueueSuccessSnackbar(isActive ? 'Template unarchived' : 'Template archived');
      await actions.refreshData();
    } catch (error) {
      notification.enqueueErrorSnackbar(
        apiErrorsWithKeys(error as AxiosError, 'Template cannot be updated right now.')
      );
    }
  };

  const cellActions: ActionConfig[] = [
    {
      id: 'edit-template',
      label: 'Edit',
      visible: row.isActive,
      onClick: () => routing.push(reverse(routes.dashboard.risks.templates.details, { templateId: row.uuid })),
    },
    {
      id: 'archive-template',
      label: 'Archive',
      visible: row.isActive,
      onClick: () => setActive(false),
      confirmation: {
        type: 'simple',
        title: 'Archive this template?',
        message: "Risk assessors can't start new assessments from it. Existing assessments are not affected.",
        confirmButtonLabel: 'Archive',
        cancelButtonLabel: 'Cancel',
        hideRecoveryWarning: true,
      },
    },
    { id: 'unarchive-template', label: 'Unarchive', visible: !row.isActive, onClick: () => setActive(true) },
  ];

  return <ActionCell actions={cellActions} recordName={row.name} />;
};

export default RiskAssessmentTemplateActionCell;
